import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { extendVariants, Select as SelectNext, SelectItem } from "@nextui-org/react";

const SelectVariant = extendVariants(SelectNext, {
  variants: {
    size: {
      md: {
        trigger: [
          'rounded-lg'
        ],
        value: [
          'text-base'
        ]
      }
    },
    color: {
      default: { // <- add a new color variant
        trigger: [ // <- Input wrapper slot
          "bg-white h-12",
          "border border-gray-500",
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[hover=true]:border-gray-500",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-gray-500",
          "group-data-[invalid=true]:border-red-400",
        ],
        listboxWrapper: [  // <- Input element slot
          "text-gray-800 text-base font-semibold",
          "placeholder:text-gray-500",
        ],
      },
      custom: { // <- add a new color variant
        trigger: [ // <- Input wrapper slot
          "bg-white",
          "border-b border-primary rounded-none",
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[editable=true]:text-gray-800 text-base font-semibold",
          "data-[placeholder=true]:text-gray-800 text-base font-semibold",
          "data-[hover=true]:border-primary",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-primary",
        ],
        label: [
          "text-primary font-semibold text-base"
        ],
        listboxWrapper: [  // <- Input element slot
          "text-gray-800 text-base font-semibold",
          "placeholder:text-gray-500",
        ],
      },
      customDanger: { // <- add a new color variant
        trigger: [ // <- Input wrapper slot
          "bg-white",
          "border-b border-danger rounded-none",
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[editable=true]:text-gray-800 text-base font-semibold",
          "data-[placeholder=true]:text-gray-800 text-base font-semibold",
          "data-[hover=true]:border-primary",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-danger",
        ],
        label: [
          "text-primary font-semibold text-base"
        ],
        listboxWrapper: [  // <- Input element slot
          "text-gray-800 text-base font-semibold",
          "placeholder:text-gray-500",
        ],
      },
    },
  },
  defaultVariants: { // <- modify/add default variants
    color: "default",
  },
});

const Select = props => {
  const { options = [], register = {}, method = 'weglot', translate = true, ...rest } = props;
  const [translatedOptions, setTranslatedOptions] = useState(options);
  const currentLanguage = typeof window !== 'undefined' && window.Weglot ? window.Weglot.getCurrentLang() : null;

  useEffect(() => {
    if (currentLanguage && translate && method === 'weglot' && typeof window !== 'undefined' && window.Weglot && options.length > 0) {
      // Prepare words array for Weglot
      const words = options.map(option => ({ "t": 1, "w": option.label }));
      
      window.Weglot.translate(
        {
          'words': words,
          'languageTo': currentLanguage
        },
        function(data) {
          if (data && data.length > 0) {
            // Map translated data back to options
            const translated = options.map((option, index) => ({
              ...option,
              label: data[index] || option.label
            }));
            setTranslatedOptions(translated);
          }
        }
      );
    } else if (!translate || method === 'i18next') {
      // If not translating with Weglot, use original options
      setTranslatedOptions(options);
    }
  }, [options, translate, method, currentLanguage]);

  return (
    <>
      <SelectVariant
        {...register}
        {...rest}
        label={null}
        aria-label="Select"
        size='md'
      >
        {translatedOptions.length > 0 && translatedOptions.map((option) => (
          <SelectItem key={option.value} aria-label={option.label} >
            {option.label}
          </SelectItem>
        ))}
      </SelectVariant>
    </>
  );
};

Select.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.string.isRequired,
  })),
  register: PropTypes.object,
  method: PropTypes.oneOf(['weglot', 'i18next']),
  translate: PropTypes.bool,
};

export default Select;
